import { useEffect, useRef } from 'react';
import {
  useAddProjectMessage,
  useAddSpecComment,
  useAddStoryComment,
  useProjectMessages,
  useSpecComments,
  useStoryComments,
} from '../api/comments.js';
import { useMe } from '../api/auth.js';
import { useMarkMessagesRead } from '../api/projects.js';
import { CommentThread } from './CommentThread.js';

/** The comment thread on the specification. */
export function SpecComments({
  projectId,
  canMarkInternal,
}: {
  projectId: string;
  canMarkInternal: boolean;
}) {
  const comments = useSpecComments(projectId);
  const add = useAddSpecComment(projectId);
  return (
    <CommentThread
      projectId={projectId}
      comments={comments.data}
      isLoading={comments.isPending}
      isError={comments.isError}
      onSubmit={(input) => add.mutate(input)}
      isSubmitting={add.isPending}
      canMarkInternal={canMarkInternal}
      emptyText="No comments on the specification yet."
      placeholder="Comment on the specification…"
    />
  );
}

/** The comment thread on a single story. */
export function StoryComments({
  projectId,
  storyId,
  canMarkInternal,
}: {
  projectId: string;
  storyId: string;
  canMarkInternal: boolean;
}) {
  const comments = useStoryComments(projectId, storyId);
  const add = useAddStoryComment(projectId, storyId);
  return (
    <CommentThread
      projectId={projectId}
      comments={comments.data}
      isLoading={comments.isPending}
      isError={comments.isError}
      onSubmit={(input) => add.mutate(input)}
      isSubmitting={add.isPending}
      canMarkInternal={canMarkInternal}
      emptyText="No comments yet — start the conversation."
      placeholder="Comment on this story…"
    />
  );
}

/** The project's general messaging feed (includes every story's comments). */
export function ProjectMessages({
  projectId,
  canMarkInternal,
  storyHref,
  autoMarkRead = false,
}: {
  projectId: string;
  canMarkInternal: boolean;
  /** Builds a link to a story, so a message about one can jump straight there. */
  storyHref?: (storyId: string) => string;
  /**
   * Mark the feed read on view (used on the founder's dedicated Messages page, so
   * opening it clears the unread dot). When off (the admin's tabbed Messages
   * section), a manual "Mark as read" button clears it instead.
   */
  autoMarkRead?: boolean;
}) {
  const { data: me } = useMe();
  const messages = useProjectMessages(projectId);
  const add = useAddProjectMessage(projectId);
  const markRead = useMarkMessagesRead(projectId);

  // With auto-mark, freeze the read cursor from the first load so "New" flags
  // survive the mark-read below; otherwise track it live (the manual button
  // clears the flags by advancing the marker).
  const frozen = useRef<string | null | undefined>(undefined);
  if (autoMarkRead && frozen.current === undefined && messages.isSuccess) {
    frozen.current = messages.data.lastReadAt;
  }
  const newSince = autoMarkRead ? (frozen.current ?? null) : (messages.data?.lastReadAt ?? null);

  // Opening the feed marks it read (once), so the unread dot clears.
  const marked = useRef(false);
  useEffect(() => {
    if (autoMarkRead && messages.isSuccess && !marked.current) {
      marked.current = true;
      markRead.mutate();
    }
  }, [autoMarkRead, messages.isSuccess, markRead]);

  return (
    <CommentThread
      projectId={projectId}
      comments={messages.data?.messages}
      isLoading={messages.isPending}
      isError={messages.isError}
      onSubmit={(input) => add.mutate(input)}
      isSubmitting={add.isPending}
      canMarkInternal={canMarkInternal}
      showSubject
      newestFirst
      // Messages that landed since this viewer last marked the feed read are
      // flagged "New"; "Mark as read" advances the marker and clears them.
      newSince={newSince}
      currentUserId={me?.id}
      storyHref={storyHref}
      emptyText="No messages yet."
      placeholder="Message the team…"
    />
  );
}
